import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Sparkles, Loader2, CheckCircle2, Wand2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { toast } from '../../../../../hooks/use-toast';
import { streamGeneratePrompt } from '../../../../channels/services/promptGenerationService';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  generatedPrompt?: string;
  promptApplied?: boolean;
}

interface BusinessDiscoveryChatProps {
  onApplyPrompt: (prompt: string) => void;
  context: {
    historial_conversaciones?: string;
    productos?: string;
    documentos_negocio?: string;
    current_prompt?: string;
  };
}

const INITIAL_MESSAGE = `¡Hola! Voy a ayudarte a crear el prompt perfecto para tu agente de WhatsApp.

He analizado la información disponible de tu negocio. Para crear un prompt efectivo, necesito hacerte algunas preguntas.

**¿Qué tipo de negocio es y a qué se dedica?**`;

function extractGeneratedPrompt(text: string): { cleanText: string; prompt: string | null } {
  const startTag = '[PROMPT_GENERADO]';
  const endTag = '[/PROMPT_GENERADO]';
  const startIdx = text.indexOf(startTag);
  const endIdx = text.indexOf(endTag);

  if (startIdx !== -1 && endIdx !== -1) {
    const prompt = text.substring(startIdx + startTag.length, endIdx).trim();
    const cleanText = (
      text.substring(0, startIdx) + text.substring(endIdx + endTag.length)
    ).trim();
    return { cleanText, prompt };
  }

  return { cleanText: text, prompt: null };
}

export function BusinessDiscoveryChat({ onApplyPrompt, context }: BusinessDiscoveryChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: INITIAL_MESSAGE },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleApplyPrompt = useCallback((prompt: string, msgIdx: number) => {
    onApplyPrompt(prompt);
    setMessages(prev => prev.map((m, i) =>
      i === msgIdx ? { ...m, promptApplied: true } : m
    ));
    toast.success('Prompt aplicado exitosamente');
  }, [onApplyPrompt]);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    // Build API messages (skip the initial hardcoded message for a cleaner context)
    const apiMessages = newMessages.map(m => ({
      role: m.role,
      content: m.content,
    }));

    let assistantContent = '';

    const cleanup = streamGeneratePrompt(
      { mode: 'discovery', messages: apiMessages, context },
      {
        onText: (text) => {
          assistantContent += text;

          const { cleanText, prompt } = extractGeneratedPrompt(assistantContent);

          setMessages(prev => {
            const last = prev[prev.length - 1];
            const newMsg: ChatMessage = {
              role: 'assistant',
              content: cleanText,
              generatedPrompt: prompt || undefined,
            };

            if (last?.role === 'assistant' && prev.length > 1 && prev[prev.length - 2]?.role === 'user') {
              return [...prev.slice(0, -1), newMsg];
            }
            return [...prev, newMsg];
          });
        },
        onDone: () => {
          setIsLoading(false);
          abortRef.current = null;

          // Final extraction check
          const { cleanText, prompt } = extractGeneratedPrompt(assistantContent);
          if (prompt) {
            setMessages(prev => {
              const updated = [...prev];
              const lastIdx = updated.length - 1;
              if (updated[lastIdx]?.role === 'assistant') {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: cleanText,
                  generatedPrompt: prompt,
                };
              }
              return updated;
            });
          }
        },
        onError: (error) => {
          setIsLoading(false);
          abortRef.current = null;
          toast.error(error);
        },
      }
    );

    abortRef.current = cleanup;
  }, [input, isLoading, messages, context]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[400px] max-h-[600px]">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Wand2 className="w-4 h-4 text-primary" />
        <h2 className="text-foreground font-semibold text-base">
          Configuración Guiada
        </h2>
        <span className="text-xs text-muted-foreground ml-auto">claude-sonnet</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-3 pr-1 min-h-0">
        {messages.map((msg, msgIdx) => (
          <div key={msgIdx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
              msg.role === 'user'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/50 text-foreground border border-border'
            }`}>
              {msg.content && (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              )}

              {/* Generated prompt block */}
              {msg.generatedPrompt && (
                <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-500/10 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-emerald-700">
                      Prompt Generado
                    </span>
                    {msg.promptApplied ? (
                      <span className="text-xs flex items-center gap-1 text-emerald-600">
                        <CheckCircle2 className="w-3 h-3" /> Aplicado
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-xs px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                        onClick={() => handleApplyPrompt(msg.generatedPrompt!, msgIdx)}
                      >
                        Aplicar Prompt
                      </Button>
                    )}
                  </div>
                  <pre className="text-xs bg-background/50 rounded p-2 overflow-x-auto whitespace-pre-wrap font-mono max-h-[200px] overflow-y-auto">
                    {msg.generatedPrompt}
                  </pre>
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && messages[messages.length - 1]?.role === 'user' && (
          <div className="flex justify-start">
            <div className="bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="text-muted-foreground">Analizando tu negocio...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex items-end gap-2 border-t border-border pt-3">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe tu negocio..."
          className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[38px] max-h-[100px]"
          rows={1}
          disabled={isLoading}
        />
        <Button
          size="icon"
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
          className="h-[38px] w-[38px] flex-shrink-0"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
